import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getModuleDetail, updateProgress, GetModuleDetailOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { CheckCircle, Circle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

type ModuleData = GetModuleDetailOutputType;
type ScenarioQ = ModuleData['scenarioQuestions'][0];

export default function LessonViewer() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ModuleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<'reading' | 'scenarios' | 'ready'>('reading');
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !id) return;
    getModuleDetail({ moduleId: id }).then(d => {
      setData(d);
      if (d.progress?.currentStep && d.progress.currentStep >= 1) {
        setStep(d.scenarioQuestions.length > 0 && d.progress.currentStep < 2 ? 'scenarios' : d.progress.currentStep >= 2 ? 'ready' : 'reading');
      }
    }).finally(() => setLoading(false));
  }, [user, id]);

  const saveStep = async (newStep: number, status?: string) => {
    if (!id) return;
    setSaving(true);
    await updateProgress({ moduleId: id, currentStep: newStep, status: status || 'In Progress' }).catch(() => {});
    setSaving(false);
  };

  const completeReading = async () => {
    await saveStep(1);
    if (data!.scenarioQuestions.length > 0) { setStep('scenarios'); }
    else { setStep('ready'); await saveStep(2); }
  };

  const nextScenario = async () => {
    if (scenarioIdx + 1 < data!.scenarioQuestions.length) {
      setScenarioIdx(i => i + 1); setSelectedAnswer(null); setRevealed(false);
    } else { setStep('ready'); await saveStep(2); }
  };

  if (loading) return (
    <div className="flex h-[calc(100vh-56px)]">
      <div className="w-64 border-r border-border bg-card p-5 space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-8" />)}</div>
      <div className="flex-1 p-8 space-y-4"><Skeleton className="h-8 w-1/2" /><Skeleton className="h-96" /></div>
    </div>
  );
  if (!data) return <div className="p-8 text-muted-foreground">Module not found.</div>;

  const hasScenarios = data.scenarioQuestions.length > 0;
  const stepDefs = [
    { key: 'reading', label: '1. Reading Material', done: step !== 'reading' },
    ...(hasScenarios ? [{ key: 'scenarios', label: '2. Scenario Practice', done: step === 'ready' }] : []),
    { key: 'ready', label: `${hasScenarios ? '3' : '2'}. Take Quiz`, done: false },
  ];

  const scenario: ScenarioQ | undefined = data.scenarioQuestions[scenarioIdx];
  const options = scenario ? [
    { key: 'A', text: scenario.optionA }, { key: 'B', text: scenario.optionB },
    { key: 'C', text: scenario.optionC }, { key: 'D', text: scenario.optionD },
  ].filter(o => o.text) : [];

  return (
    <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col shrink-0">
        <div className="p-5 border-b border-border">
          <Link to="/modules" className="text-xs text-muted-foreground flex items-center gap-1 mb-3 hover:text-foreground"><ChevronLeft className="w-3 h-3" /> Back to modules</Link>
          <p className="text-xs text-muted-foreground mb-0.5">{data.module.categoryName}</p>
          <p className="font-semibold text-foreground text-sm leading-snug">{data.module.title}</p>
          {data.module.estimatedDuration && <p className="text-xs text-muted-foreground mt-1">{data.module.estimatedDuration}</p>}
        </div>
        <nav className="p-4 flex flex-col gap-1.5">
          {stepDefs.map(s => (
            <div key={s.key} className={`flex items-center gap-3 px-3 py-2.5 ${step === s.key ? 'bg-primary/10 border border-primary/20' : ''}`}>
              {s.done ? <CheckCircle className="w-5 h-5 text-primary shrink-0" /> : <Circle className={`w-5 h-5 shrink-0 ${step === s.key ? 'text-primary' : 'text-muted-foreground'}`} />}
              <span className={`text-sm ${step === s.key ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{s.label}</span>
            </div>
          ))}
        </nav>
        {data.progress && (
          <div className="mt-auto p-4 border-t border-border">
            <p className="text-xs text-muted-foreground">Status: <span className="text-foreground">{data.progress.status}</span></p>
            {data.progress.quizScore !== null && <p className="text-xs text-muted-foreground mt-0.5">Best score: <span className="text-foreground">{data.progress.quizScore}%</span></p>}
          </div>
        )}
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-y-auto bg-background p-8">
        {step === 'reading' && (
          <div>
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-widest">Reading Material</p>
            <h2 className="text-xl font-bold text-foreground mb-6">{data.module.title}</h2>
            {data.module.description && <p className="text-muted-foreground mb-6">{data.module.description}</p>}
            {data.module.contentFiles.length === 0 ? (
              <div className="border border-dashed border-border p-10 text-center text-muted-foreground mb-6">
                No reading materials have been uploaded yet. Click continue to proceed.
              </div>
            ) : (
              <div className="space-y-4 mb-6">
                {data.module.contentFiles.map((f, i) => (
                  <div key={i} className="border border-border">
                    <div className="flex items-center justify-between px-4 py-2 bg-muted border-b border-border">
                      <span className="text-xs text-muted-foreground font-medium">Document {i + 1}</span>
                      <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1 hover:underline"><ExternalLink className="w-3 h-3" /> Open in new tab</a>
                    </div>
                    <iframe src={f.url} className="w-full border-0" style={{ height: '520px' }} title={`Document ${i + 1}`} />
                  </div>
                ))}
              </div>
            )}
            <Button onClick={completeReading} disabled={saving} className="bg-primary text-primary-foreground px-8">
              Mark as read & continue <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}

        {step === 'scenarios' && scenario && (
          <div className="max-w-2xl">
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-widest">Scenario Practice — {scenarioIdx + 1} of {data.scenarioQuestions.length}</p>
            <h2 className="text-xl font-bold text-foreground mb-6">What would you do?</h2>
            <p className="text-foreground mb-6 leading-relaxed text-lg">{scenario.question}</p>
            <div className="flex flex-col gap-3 mb-6">
              {options.map(opt => {
                let style = 'border border-border bg-card text-foreground hover:border-primary/50';
                const correctKey = scenario.correctAnswer || 'B';
                if (revealed) {
                  if (opt.key === selectedAnswer && opt.key !== correctKey) style = 'border border-destructive bg-destructive/10 text-destructive';
                  else if (opt.key === correctKey) style = 'border border-primary bg-primary/10 text-primary';
                } else if (selectedAnswer === opt.key) style = 'border border-primary bg-primary/10 text-foreground';
                return (
                  <button key={opt.key} onClick={() => !revealed && setSelectedAnswer(opt.key)}
                    className={`px-5 py-4 text-left text-sm flex items-start gap-3 transition-colors ${style}`}>
                    <span className="w-6 h-6 border border-current flex items-center justify-center text-xs font-bold shrink-0">{opt.key}</span>
                    {opt.text}
                  </button>
                );
              })}
            </div>
            {!revealed ? (
              <Button onClick={() => setRevealed(true)} disabled={!selectedAnswer} className="bg-primary text-primary-foreground">Check answer</Button>
            ) : (
              <div>
                <p className="text-sm text-muted-foreground mb-4">{selectedAnswer === (scenario.correctAnswer || 'B') ? '✅ Correct! ' : `❌ The best answer is ${scenario.correctAnswer || 'B'}. `}The correct approach is always to prioritise safety, follow procedure, and communicate clearly.</p>
                <Button onClick={nextScenario} className="bg-primary text-primary-foreground">
                  {scenarioIdx + 1 < data.scenarioQuestions.length ? 'Next scenario' : 'Continue to quiz'} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {step === 'ready' && (
          <div className="max-w-lg">
            <p className="text-xs text-muted-foreground mb-1 uppercase tracking-widest">Knowledge Check</p>
            <h2 className="text-xl font-bold text-foreground mb-4">Ready to take the quiz?</h2>
            <p className="text-muted-foreground mb-2">{data.standardQuestions.length} questions · Pass mark: {data.module.passMarkPercent}%</p>
            <p className="text-muted-foreground text-sm mb-8">You can retake the quiz as many times as needed. Your score is recorded for each attempt.</p>
            {data.standardQuestions.length === 0 ? (
              <p className="text-muted-foreground border border-dashed border-border p-6 text-sm">No quiz questions have been added to this module yet.</p>
            ) : (
              <Button onClick={() => navigate(`/modules/${id}/quiz`)} className="bg-primary text-primary-foreground px-8 text-base">
                Start Quiz <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {data.progress?.quizAttempts ? (
              <p className="text-xs text-muted-foreground mt-4">Previous attempts: {data.progress.quizAttempts} · Best score: {data.progress.quizScore}%</p>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}
